/* eslint-disable @typescript-eslint/naming-convention */
import { ID } from '@datorama/akita';
import { Polygon } from 'ol/geom';

import { Column, Entity } from '../entity/index';
import { IEntity } from '../interfaces/models';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import {
  formatAddressName,
  formatStreetName,
  formatStreetNumber,
} from '../lib/utils';

export interface ID2CGeo extends IEntity {
  geometry?: Polygon;
  geometry_name: string;
  properties: ID2CGeoFeatureProperties;
  type: string;
  id: string;
}

export interface ID2CGeoFeatureProperties {
  city: string;
  coid: string;
  created_at: Date;
  created_by: string;
  derived_drop_permission_status: string;
  dir_prefix: string;
  dir_suffix: string;
  do_not_knock: boolean;
  dp_walklist_address_seq: number;
  dp_walklist_id: number;
  floor: string;
  fsa: string;
  is_deleted: boolean;
  language: string;
  last_knock_status: string;
  modified_at: Date;
  modified_by: string;
  outcome_id: string;
  postal_code: string;
  premise_type: string;
  province: string;
  sales_premise_type_rco_jira: string;
  sales_walklist_address_seq: number;
  sales_walklist_id: number;
  sayb_d2c_decision: string;
  street_name: string;
  street_number: string;
  street_number_prefix: string;
  street_number_suffix: string;
  street_type_prefix: string;
  street_type_suffix: string;
  unit: string;
}

export interface ID2CGeoServiceAddress extends IEntity {
  serviceAddress: string;
  unitNumber: string;
  streetNumber: string;
  streetName: string;
  postalCode: string;
  sayb: string;
  walklistAssigned: string;
  dnk: boolean;
  city: string;
  premiseType: string;
  dpStatus: string;
  fsa: string;
  salesWalklistId?: number;
  dpWalklistId?: number;
}

export interface ID2CGeoServiceUniqueResult {
  type: string;
  properties: {
    value: any;
  };
  id: ID;
}

@Entity({
  name: PageTitles.d2cgeo,
})
export class D2CGeo implements ID2CGeo {
  id: string;
  geometry_name: string;
  properties: ID2CGeoFeatureProperties;
  type: string;
  createdAt?: Date;
  modifiedAt?: Date;
  createdBy?: string;
  modifiedBy?: string;
  href?: string;

  constructor(
    territory: ID2CGeo = {
      id: '',
      geometry_name: '',
      type: '',
      createdAt: new Date(),
      href: '',
      modifiedAt: new Date(),
      createdBy: '',
      modifiedBy: '',
      properties: null,
    }
  ) {
    const {} = territory;
  }
}

@Entity({
  name: PageTitles.d2Cgeoserviceaddress,
})
export class D2CGeoServiceAddress implements ID2CGeoServiceAddress {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  unitNumber: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  streetNumber: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
    },
  })
  streetName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  postalCode: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
  })
  sayb: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
    },
  })
  walklistAssigned: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 7,
      hide: true,
    },
  })
  dnk: boolean;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 8,
      hide: true,
    },
  })
  city: string;

  @Column({
    type: ColumnType.string,

    table: {
      defaultPosition: 9,
      hide: true,
    },
  })
  premiseType: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 10,
      hide: true,
    },
  })
  fsa: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 11,
      hide: true,
    },
  })
  dpStatus: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 12,
      hide: true,
    },
  })
  serviceAddress: string;

  id: ID;
  createdAt?: Date;
  modifiedAt?: Date;
  createdBy?: string;
  modifiedBy?: string;
  href?: string;
  salesWalklistId?: number;
  dpWalklistId?: number;

  constructor(
    territory: ID2CGeoServiceAddress = {
      id: '',
      serviceAddress: '',
      unitNumber: '',
      streetNumber: '',
      streetName: '',
      postalCode: '',
      sayb: '',
      walklistAssigned: '',
      dnk: false,
      city: '',
      premiseType: '',
      dpStatus: '',
      fsa: '',
      salesWalklistId: null,
      dpWalklistId: null,
    }
  ) {
    const {
      id,
      serviceAddress,
      unitNumber,
      streetNumber,
      streetName,
      postalCode,
      sayb,
      walklistAssigned: walklistassigned,
      dnk,
      city,
      premiseType,
      dpStatus,
      fsa,
      dpWalklistId,
      salesWalklistId,
    } = territory;

    this.id = id;
    this.serviceAddress = serviceAddress;
    this.unitNumber = unitNumber;
    this.streetName = streetName;
    this.streetNumber = streetNumber;
    this.postalCode = postalCode;
    this.sayb = sayb;
    this.walklistAssigned = walklistassigned;
    this.dnk = dnk;
    this.city = city;
    this.premiseType = premiseType;
    this.dpStatus = dpStatus;
    this.fsa = fsa;
    this.dpWalklistId = dpWalklistId;
    this.salesWalklistId = salesWalklistId;
  }

  static createFromDTO(
    record: ID2CGeoFeatureProperties,
    id: string
  ): D2CGeoServiceAddress {
    const address = {
      addressId: id.split('.')[0],
      buildingType: '',
      projectSubType: '',
      locationComments: null,
      city: record.city,
      dirPrefix: record.dir_prefix,
      dirSuffix: record.dir_suffix,
      floor: record.floor,
      postalCode: record.postal_code,
      province: record.province,
      streetName: record.street_name,
      streetNumber: record.street_number,
      streetNumberPrefix: record.street_number_prefix,
      streetNumberSuffix: record.street_number_suffix,
      streetTypePrefix: record.street_type_prefix,
      streetTypeSuffix: record.street_type_suffix,
      unit: record.unit,
    };

    const formattedAddress = formatAddressName(address);
    const streetName = formatStreetName(address);
    const streetNumber = formatStreetNumber(address);

    return new D2CGeoServiceAddress({
      id: id.split('.')[1],
      unitNumber: record.unit,
      streetNumber,
      streetName,
      postalCode: record.postal_code,
      sayb: record.sayb_d2c_decision,
      walklistAssigned: '',
      dnk: record.do_not_knock,
      city: record.city,
      premiseType: record.premise_type,
      dpStatus: record.derived_drop_permission_status,
      serviceAddress: formattedAddress,
      fsa: record.fsa,
    });
  }
}
